// Error normalization for the proposals page.
//
// Every offer operation used to fail into the same opaque sentence, which hid
// the reason the backend actually gave (permission, validation, stale state).
// This module turns an axios-style rejection into the most specific sentence we
// can honestly show, falling back to the caller's message only when the backend
// said nothing useful.

const firstString = value => {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (Array.isArray(value)) {
    const found = value.map(firstString).find(Boolean);
    return found || '';
  }
  if (value && typeof value === 'object') {
    const found = Object.values(value).map(firstString).find(Boolean);
    return found || '';
  }
  return '';
};

export const STATUS_FALLBACK_MESSAGES = Object.freeze({
  401: 'Sua sessão expirou. Entre novamente para continuar.',
  403: 'Você não tem permissão para executar esta ação nesta proposta.',
  404: 'Esta proposta não está mais disponível.',
  409: 'Esta proposta mudou enquanto você trabalhava nela. Recarregue para ver a versão mais recente.',
  422: 'Não foi possível concluir: os dados enviados foram recusados.',
  429: 'Muitas tentativas seguidas. Aguarde alguns instantes e tente de novo.',
});

// Backend payloads answer with `errors`, `error`, `message` or `full_messages`
// depending on the controller. Read them in that order before giving up.
export const apiErrorMessage = (error, fallback = '') => {
  const data = error?.response?.data;
  const fromPayload =
    firstString(data?.errors) ||
    firstString(data?.error) ||
    firstString(data?.message) ||
    firstString(data?.full_messages);
  if (fromPayload) return fromPayload;

  const status = Number(error?.response?.status) || 0;
  if (STATUS_FALLBACK_MESSAGES[status]) return STATUS_FALLBACK_MESSAGES[status];
  if (status >= 500) {
    return 'O servidor não respondeu como esperado. Tente novamente em instantes.';
  }
  if (error && !error.response) {
    return 'Não foi possível falar com o servidor. Verifique sua conexão e tente novamente.';
  }

  return fallback;
};

export const isConflictError = error => Number(error?.response?.status) === 409;

export const isUnavailableFeatureError = error =>
  [403, 404].includes(Number(error?.response?.status));
